/**
 * Register efektov. Kľúč je **ID definície karty** (`bigfoot.savagery`), takže
 * rovnaká karta v inom balíčku má vlastný záznam a nič sa neprekrýva.
 *
 * Postavy pribúdajú po jednej: každá je unikátna a jej karty treba prečítať
 * a napísať ručne. Karta bez záznamu funguje ako doteraz — len hodnota a boost.
 */

import { damageGuards, damagedHooks, defeatHooks, discardHooks, movedHooks, ongoingEndHooks } from '../ops.js';
import { achillesCards, achillesSpecial } from './achilles.js';
import { aliceCards, aliceSpecial } from './alice.js';
import { angelCards, angelSpecial } from './angel.js';
import { annieCards, annieSpecial } from './annie.js';
import { beowulfCards, beowulfSpecial } from './beowulf.js';
import { bigfootCards, bigfootSpecial } from './bigfoot.js';
import { bloodyMaryCards, bloodyMarySpecial } from './bloodyMary.js';
import { bruceLeeCards, bruceLeeSpecial } from './bruceLee.js';
import { bullseyeCards } from './bullseye.js';
import { genieCards, genieSpecial } from './genie.js';
import { blackPantherCards, blackPantherSpecial } from './blackPanther.js';
import { ciriCards, ciriSpecial } from './ciri.js';
import { draculaCards, draculaSpecial } from './dracula.js';
import { eredinCards, eredinSpecial } from './eredin.js';
import { houdiniCards, houdiniSpecial } from './houdini.js';
import { leshenCards, leshenSpecial } from './leshen.js';
import { invisibleManCards, invisibleManSpecial } from './invisibleMan.js';
import { goldenBatCards, goldenBatSpecial } from './goldenBat.js';
import { lukeCageCards, lukeCageSpecial } from './lukeCage.js';
import { msMarvelCards, msMarvelSpecial } from './msMarvel.js';
import { doctorStrangeCards, doctorStrangeSpecial } from './doctorStrange.js';
import { jekyllHydeCards, jekyllHydeSpecial } from './jekyllHyde.js';
import { teslaCards, teslaSpecial } from './tesla.js';
import { hamletCards, hamletSpecial } from './hamlet.js';
import { sherlockCards, sherlockSpecial } from './sherlock.js';
import { sunWukongCards, sunWukongSpecial } from './sunWukong.js';
import { ghostRiderCards, ghostRiderSpecial } from './ghostRider.js';
import { kingArthurCards, kingArthurSpecial } from './kingArthur.js';
import { willowCards, willowSpecial } from './willow.js';
import { sheHulkCards, sheHulkSpecial } from './sheHulk.js';
import { spiderManCards, spiderManSpecial } from './spiderMan.js';
import { tomoeCards, tomoeSpecial } from './tomoe.js';
import { winterSoldierCards, winterSoldierSpecial } from './winterSoldier.js';
import { medusaCards, medusaSpecial } from './medusa.js';
import { philippaCards, philippaSpecial } from './philippa.js';
import { robinHoodCards, robinHoodSpecial } from './robinHood.js';
import { odaCards, odaSpecial } from './oda.js';
import { sinbadCards, sinbadSpecial } from './sinbad.js';
import { tRexCards, tRexSpecial } from './tRex.js';
import { yenneferCards, yenneferSpecial } from './yennefer.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

export const EFFECTS: EffectRegistry = {
  ...bigfootCards,
  ...sinbadCards,
  ...achillesCards,
  ...invisibleManCards,
  ...medusaCards,
  ...odaCards,
  ...houdiniCards,
  ...ciriCards,
  ...eredinCards,
  ...yenneferCards,
  ...aliceCards,
  ...leshenCards,
  ...angelCards,
  ...annieCards,
  ...beowulfCards,
  ...blackPantherCards,
  ...philippaCards,
  ...robinHoodCards,
  ...tRexCards,
  ...draculaCards,
  ...lukeCageCards,
  ...bruceLeeCards,
  ...goldenBatCards,
  ...bloodyMaryCards,
  ...msMarvelCards,
  ...bullseyeCards,
  ...genieCards,
  ...winterSoldierCards,
  ...spiderManCards,
  ...sheHulkCards,
  ...tomoeCards,
  ...doctorStrangeCards,
  ...jekyllHydeCards,
  ...teslaCards,
  ...sherlockCards,
  ...hamletCards,
  ...sunWukongCards,
  ...ghostRiderCards,
  ...kingArthurCards,
  ...willowCards,
};

export const HERO_SPECIALS: Record<string, HeroSpecial> = {
  bigfoot: bigfootSpecial,
  sinbad: sinbadSpecial,
  achilles: achillesSpecial,
  'invisible-man': invisibleManSpecial,
  medusa: medusaSpecial,
  'oda-nobunaga': odaSpecial,
  houdini: houdiniSpecial,
  ciri: ciriSpecial,
  eredin: eredinSpecial,
  yennefer: yenneferSpecial,
  alice: aliceSpecial,
  'ancient-leshen': leshenSpecial,
  angel: angelSpecial,
  'annie-christmas': annieSpecial,
  beowulf: beowulfSpecial,
  'black-panther': blackPantherSpecial,
  philippa: philippaSpecial,
  'robin-hood': robinHoodSpecial,
  't-rex': tRexSpecial,
  dracula: draculaSpecial,
  'luke-cage': lukeCageSpecial,
  'bruce-lee': bruceLeeSpecial,
  'golden-bat': goldenBatSpecial,
  'bloody-mary': bloodyMarySpecial,
  'ms-marvel': msMarvelSpecial,
  'the-genie': genieSpecial,
  'winter-soldier': winterSoldierSpecial,
  'spider-man': spiderManSpecial,
  'she-hulk': sheHulkSpecial,
  'tomoe-gozen': tomoeSpecial,
  'doctor-strange': doctorStrangeSpecial,
  'jekyll-hyde': jekyllHydeSpecial,
  'nikola-tesla': teslaSpecial,
  'sherlock-holmes': sherlockSpecial,
  hamlet: hamletSpecial,
  'sun-wukong': sunWukongSpecial,
  'ghost-rider': ghostRiderSpecial,
  'king-arthur': kingArthurSpecial,
  willow: willowSpecial,
};

// Porazená figúrka → schopnosť jej majiteľa (Patroklos). Hook, nie import
// v ops.ts, inak by vznikol kruh ops → effects → ops.
// Kartu odhodil súperov efekt → reakcia karty (And the Beautiful Bess).
discardHooks.push((ctx, player, cardId) => {
  const defId = ctx.state.cards[cardId]?.defId;
  if (defId) EFFECTS[defId]?.onDiscardedByOpponent?.(ctx, player, cardId);
});

// Trvalá schéma chráni figúrky majiteľa (Might of the Aen Elle).
damageGuards.push((ctx, fighter, source) => {
  const ongoing = ctx.state.players[fighter.owner]?.ongoing;
  const effect = ongoing ? EFFECTS[ctx.state.cards[ongoing]?.defId ?? ''] : undefined;
  return Boolean(effect?.ongoing?.preventsDamage?.(ctx, fighter.owner, fighter, source));
});

damagedHooks.push((ctx, fighter, amount) => {
  const heroId = ctx.state.players[fighter.owner]?.heroId;
  if (heroId) HERO_SPECIALS[heroId]?.onDamaged?.(ctx, fighter.owner, fighter, amount);
});

ongoingEndHooks.push((ctx, player, cardId) => {
  EFFECTS[ctx.state.cards[cardId]?.defId ?? '']?.ongoing?.onEnd?.(ctx, player);
});

movedHooks.push((ctx, fighter, path) => {
  ctx.state.players.forEach((seat, player) => {
    if (!seat.eliminated) HERO_SPECIALS[seat.heroId]?.onFighterMoved?.(ctx, player, fighter, path);
  });
});

defeatHooks.push((ctx, fighter) => {
  const heroId = ctx.state.players[fighter.owner]?.heroId;
  if (heroId) HERO_SPECIALS[heroId]?.onDefeated?.(ctx, fighter.owner, fighter);
});

export function effectOf(defId: string | undefined): CardEffect | undefined {
  return defId ? EFFECTS[defId] : undefined;
}

/** Ktoré postavy majú efekty naozaj implementované — UI to hráčovi povie. */
export const IMPLEMENTED_HEROES: readonly string[] = [
  'bigfoot',
  'sinbad',
  'achilles',
  'invisible-man',
  'medusa',
  'oda-nobunaga',
  'houdini',
  'ciri',
  'eredin',
  'yennefer',
  'alice',
  'ancient-leshen',
  'angel',
  'annie-christmas',
  'beowulf',
  'black-panther',
  'philippa',
  'robin-hood',
  't-rex',
  'dracula',
  'luke-cage',
  'bruce-lee',
  'golden-bat',
  'bloody-mary',
  'ms-marvel',
  'bullseye',
  'the-genie',
  'winter-soldier',
  'spider-man',
  'she-hulk',
  'tomoe-gozen',
  'doctor-strange',
  'jekyll-hyde',
  'nikola-tesla',
  'sherlock-holmes',
  'hamlet',
  'sun-wukong',
  'ghost-rider',
  'king-arthur',
  'willow',
];

export type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';
export { effectiveValue, type CardValue } from './preview.js';
